import { Component } from 'react'
import styled from 'styled-components'
import * as FiIcons from 'react-icons/fi'
import { Config } from '../Global'
import { getTitoloI18n } from '../Utils/titoloUtils'

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.theme.colors.shadow.overlay};
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`

const ModalContainer = styled.div`
  background: ${props => props.theme.colors.background.primary};
  border-radius: ${props => props.theme.radius.card};
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px ${props => props.theme.colors.shadow.strong};
  transition: background 0.3s ease;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.scrollbar.track};
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.scrollbar.thumb};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.scrollbar.thumbHover};
  }
`

const ModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${props => props.theme.colors.border.medium};
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  background: ${props => props.theme.colors.background.primary};
  z-index: 10;
  transition: all 0.3s ease;
`

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  font-family: ${props => props.theme.typography.fontFamily};
  color: ${props => props.theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: color 0.3s ease;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.text.tertiary};
  transition: ${props => props.theme.transitions.default};
  border-radius: ${props => props.theme.radius.button};

  &:hover {
    background: ${props => props.theme.colors.background.hover};
    color: ${props => props.theme.colors.text.primary};
  }
`

const ModalBody = styled.div`
  padding: 1.5rem;
`

const Section = styled.div`
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
`

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  font-family: ${props => props.theme.typography.fontFamily};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid ${props => props.theme.colors.border.medium};
  transition: all 0.3s ease;
`

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const InfoLabel = styled.span`
  font-size: ${props => props.theme.typography.fontSize.label};
  font-family: ${props => props.theme.typography.fontFamily};
  color: ${props => props.theme.colors.text.tertiary};
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: color 0.3s ease;
`

const InfoValue = styled.span`
  font-size: ${props => props.theme.typography.fontSize.input};
  font-family: ${props => props.theme.typography.fontFamily};
  color: ${props => props.theme.colors.text.primary};
  font-weight: 500;
  transition: color 0.3s ease;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${props => props.theme.typography.fontSize.input};
  font-family: ${props => props.theme.typography.fontFamily};
`

const Thead = styled.thead`
  background: ${props => props.theme.colors.background.secondary};
  transition: background 0.3s ease;
`

const Th = styled.th`
  text-align: left;
  padding: 0.75rem;
  font-weight: 600;
  font-family: ${props => props.theme.typography.fontFamily};
  color: ${props => props.theme.colors.text.primary};
  border-bottom: 2px solid ${props => props.theme.colors.border.medium};
  font-size: ${props => props.theme.typography.fontSize.label};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: all 0.3s ease;
`

const Tbody = styled.tbody``

const Tr = styled.tr`
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
  transition: all 0.3s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${props => props.theme.colors.background.hover};
  }
`

const Td = styled.td`
  padding: 0.75rem;
  font-family: ${props => props.theme.typography.fontFamily};
  color: ${props => props.theme.colors.text.secondary};
  transition: color 0.3s ease;
`

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
`

const SummaryBox = styled.div`
  padding: 1rem;
  background: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.radius.input};
  border-left: 3px solid ${props => props.$borderColor || props.theme.colors.semantic.primary};
  transition: all 0.3s ease;

  &.total {
    border-width: 3px;
  }
`

const SummaryLabel = styled.div`
  font-size: ${props => props.theme.typography.fontSize.label};
  font-family: ${props => props.theme.typography.fontFamily};
  color: ${props => props.theme.colors.text.tertiary};
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: color 0.3s ease;
`

const SummaryValue = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  font-family: ${props => props.theme.typography.fontFamily};
  color: ${props => props.theme.colors.text.primary};
  transition: color 0.3s ease;

  &.total {
    font-size: 1.5rem;
  }
`

const ModalFooter = styled.div`
  padding: 1.5rem;
  border-top: 1px solid ${props => props.theme.colors.border.medium};
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  position: sticky;
  bottom: 0;
  background: ${props => props.theme.colors.background.primary};
  z-index: 10;
  transition: all 0.3s ease;

  @media (max-width: 500px) {
    flex-direction: column-reverse;
  }
`

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: ${props => props.theme.radius.button};
  font-size: ${props => props.theme.typography.fontSize.button};
  font-weight: ${props => props.theme.typography.fontWeight.button};
  font-family: ${props => props.theme.typography.fontFamily};
  cursor: pointer;
  transition: ${props => props.theme.transitions.button};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;

  &.secondary {
    background: ${props => props.theme.colors.background.primary};
    color: ${props => props.theme.colors.text.primary};
    border: 1px solid ${props => props.theme.colors.border.medium};

    &:hover {
      background: ${props => props.theme.colors.background.hover};
      border-color: ${props => props.theme.colors.border.strong};
    }
  }

  &.primary {
    background: ${props => props.theme.colors.semantic.primary};
    color: ${props => props.theme.colors.text.inverse};

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px ${props => props.theme.colors.shadow.medium};
      opacity: 0.9;
    }
  }

  @media (max-width: 500px) {
    width: 100%;
  }
`

const ItemDescription = styled.div`
  font-size: ${props => props.theme.typography.fontSize.label};
  font-family: ${props => props.theme.typography.fontFamily};
  color: ${props => props.theme.colors.text.tertiary};
  margin-top: 0.25rem;
  transition: color 0.3s ease;
`

export default class PreviewModal extends Component {
  render () {
    const { isOpen, onClose, onConfirm, data } = this.props

    if (!isOpen || !data) return null

    const {
      cliente,
      articoli = [],
      servizi = [],
      importo = 0,
      subtotale = 0,
      scontoApplicato = 0,
      totaleIva = 0,
      totaleFattura = 0,
      extraMateriale = 0,
      extraServizi = 0,
      giorniScadenza = 30,
      dataScadenza
    } = data

    return (
      <ModalOverlay onClick={onClose}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>
              <FiIcons.FiFileText size={24} />
              Anteprima Fattura
            </ModalTitle>
            <CloseButton onClick={onClose}>
              <FiIcons.FiX size={20} />
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            {/* Info Header */}
            <Section>
              <SectionTitle>
                <FiIcons.FiInfo />
                Informazioni Generali
              </SectionTitle>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>Cliente</InfoLabel>
                  <InfoValue>{cliente}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Tipo</InfoLabel>
                  <InfoValue>Da Preventivo</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Giorni Scadenza</InfoLabel>
                  <InfoValue>{giorniScadenza} giorni</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Data Scadenza</InfoLabel>
                  <InfoValue>
                    {dataScadenza
                      ? new Date(dataScadenza).toLocaleDateString('it-IT', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })
                      : '-'}
                  </InfoValue>
                </InfoItem>
              </InfoGrid>
            </Section>

            {/* Articoli */}
            {articoli.length > 0 && (
              <Section>
                <SectionTitle>
                  <FiIcons.FiPackage />
                  Articoli ({articoli.length})
                </SectionTitle>
                <Table>
                  <Thead>
                    <Tr>
                      <Th>Titolo</Th>
                      <Th>Qta</Th>
                      <Th>U.M.</Th>
                      <Th>Prezzo</Th>
                      <Th>IVA %</Th>
                      <Th>Totale</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {articoli.map((articolo, i) => (
                      <Tr key={i}>
                        <Td>
                          <strong>{getTitoloI18n(articolo)}</strong>
                          {articolo.descrizione && (
                            <ItemDescription>
                              {articolo.descrizione}
                            </ItemDescription>
                          )}
                        </Td>
                        <Td>{articolo.qta}</Td>
                        <Td>{articolo.unitaMisura || '-'}</Td>
                        <Td>{Config.format(articolo.prezzoUnitario)}</Td>
                        <Td>{articolo.iva}%</Td>
                        <Td><strong>{Config.format(articolo.totale)}</strong></Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Section>
            )}

            {/* Servizi */}
            {servizi.length > 0 && (
              <Section>
                <SectionTitle>
                  <FiIcons.FiTool />
                  Servizi ({servizi.length})
                </SectionTitle>
                <Table>
                  <Thead>
                    <Tr>
                      <Th>Titolo</Th>
                      <Th>U.M.</Th>
                      <Th>Qta</Th>
                      <Th>Costo</Th>
                      <Th>IVA %</Th>
                      <Th>Totale</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {servizi.map((servizio, i) => (
                      <Tr key={i}>
                        <Td>
                          <strong>{servizio.titolo}</strong>
                          {servizio.descrizione && (
                            <ItemDescription>
                              {servizio.descrizione}
                            </ItemDescription>
                          )}
                        </Td>
                        <Td>{servizio.unitaMisura || '-'}</Td>
                        <Td>{servizio.qta}</Td>
                        <Td>{Config.format(servizio.costoUnitario)}</Td>
                        <Td>{servizio.iva}%</Td>
                        <Td><strong>{Config.format(servizio.totale)}</strong></Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Section>
            )}

            {/* Totali */}
            <Section>
              <SectionTitle>
                <FiIcons.FiDollarSign />
                Riepilogo Importi
              </SectionTitle>
              <SummaryGrid>
                <SummaryBox $borderColor='#000'>
                  <SummaryLabel>Importo Preventivato</SummaryLabel>
                  <SummaryValue>{Config.format(importo)}</SummaryValue>
                </SummaryBox>
                <SummaryBox $borderColor='#666'>
                  <SummaryLabel>Extra Materiale</SummaryLabel>
                  <SummaryValue>{Config.format(extraMateriale)}</SummaryValue>
                </SummaryBox>
                <SummaryBox $borderColor='#666'>
                  <SummaryLabel>Extra Servizi</SummaryLabel>
                  <SummaryValue>{Config.format(extraServizi)}</SummaryValue>
                </SummaryBox>
              </SummaryGrid>

              <div style={{ marginTop: '1rem' }}>
                <SummaryGrid>
                  <SummaryBox $borderColor='#000'>
                    <SummaryLabel>Subtotale</SummaryLabel>
                    <SummaryValue>{Config.format(subtotale)}</SummaryValue>
                  </SummaryBox>
                  <SummaryBox $borderColor='#666'>
                    <SummaryLabel>Sconto Applicato</SummaryLabel>
                    <SummaryValue>{Config.format(scontoApplicato)}</SummaryValue>
                  </SummaryBox>
                  <SummaryBox $borderColor='#666'>
                    <SummaryLabel>Totale IVA</SummaryLabel>
                    <SummaryValue>{Config.format(totaleIva)}</SummaryValue>
                  </SummaryBox>
                  <SummaryBox className='total' $borderColor='#000'>
                    <SummaryLabel>Totale Fattura</SummaryLabel>
                    <SummaryValue className='total'>{Config.format(totaleFattura + extraMateriale + extraServizi)}</SummaryValue>
                  </SummaryBox>
                </SummaryGrid>
              </div>
            </Section>
          </ModalBody>

          <ModalFooter>
            <Button className='secondary' onClick={onClose}>
              <FiIcons.FiX />
              Annulla
            </Button>
            <Button className='primary' onClick={onConfirm}>
              <FiIcons.FiCheck />
              Conferma Generazione
            </Button>
          </ModalFooter>
        </ModalContainer>
      </ModalOverlay>
    )
  }
}
